import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Trash2, ShoppingBag, ArrowRight, Minus, Plus, X, Check, ImageOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguageStore, useAuthStore, useWishlistStore, useQuoteStore, getLocalized } from '../store';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const RAW_TAGS_MAP = {
  en: {
    wood: 'Solid Wood', mdf: 'MDF Panels', chipboard: 'Chipboard', melamine: 'Melamine', acrylic: 'Acrylic',
    aluminum: 'Aluminum Profile', glass: 'Premium Glass', mirror: 'Mirror', stone: 'Natural Stone',
    natural_veneer: 'Natural Veneer', engineered_veneer: 'Modified Veneer', hpl: 'HPL Panels',
    leather: 'Natural Leather', eco_leather: 'Eco Leather', fabric: 'Fabric / Textiles',
    primer: 'Primer', paint: 'Paint', lacquer: 'Lacquer',
    matte: 'Matte', glossy: 'Glossy', satin: 'Satin', textured: 'Textured', natural: 'Natural', brushed: 'Brushed'
  },
  ru: {
    wood: 'Натуральное Дерево', mdf: 'МДФ Панели', chipboard: 'ДСП / ЛДСП', melamine: 'Меламин', acrylic: 'Акрил',
    aluminum: 'Алюминиевый Профиль', glass: 'Премиальное Стекло', mirror: 'Зеркальное Полотно', stone: 'Натуральный Камень',
    natural_veneer: 'Натуральный Шпон', engineered_veneer: 'Модифицированный Шпон', hpl: 'HPL Панели',
    leather: 'Натуральная Кожа', eco_leather: 'Экокожа', fabric: 'Интерьерный Текстиль',
    primer: 'Архитектурный Грунт', paint: 'Полиуретановая Эмаль', lacquer: 'Защитный Лак',
    matte: 'Матовая', glossy: 'Глянцевая', satin: 'Сатинированная (Сатин)', textured: 'Текстурированная', natural: 'Натуральная фактура', brushed: 'Брашированная'
  },
  nl: {
    wood: 'Massief Hout', mdf: 'MDF Panelen', chipboard: 'Spaanplaat', melamine: 'Melamine', acrylic: 'Acryl',
    aluminum: 'Aluminium', glass: 'Premium Glas', mirror: 'Spiegel', stone: 'Natuursteen',
    natural_veneer: 'Natuurlijk Fineer', engineered_veneer: 'Gemodificeerd Fineer', hpl: 'HPL Panelen',
    leather: 'Natuurlijk Leer', eco_leather: 'Eco-leer', fabric: 'Stof / Textiel',
    primer: 'Primer', paint: 'Polyurethaanlak', lacquer: 'Beschermende Lak',
    matte: 'Mat', glossy: 'Glanzend', satin: 'Satijn', textured: 'Getextureerd', natural: 'Natuurlijk', brushed: 'Geborsteld'
  },
  ro: {
    wood: 'Lemn Masiv', mdf: 'Panouri MDF', chipboard: 'PAL', melamine: 'Melamină', acrylic: 'Acrilic',
    aluminum: 'Aluminiu', glass: 'Sticlă Premium', mirror: 'Oglindă', stone: 'Piatră Naturală',
    natural_veneer: 'Furnir Natural', engineered_veneer: 'Furnir Modificat', hpl: 'Panouri HPL',
    leather: 'Piele Naturală', eco_leather: 'Piele Ecologică', fabric: 'Material / Textile',
    primer: 'Grund', paint: 'Vopsea Poliuretanică', lacquer: 'Lac Protecție',
    matte: 'Mată', glossy: 'Lucioasă', satin: 'Satinată', textured: 'Texturată', natural: 'Naturală', brushed: 'Satinat'
  }
};

export const WishlistPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const { isAuthenticated } = useAuthStore();
  const { items, removeFromWishlist, fetchWishlist } = useWishlistStore();
  const { addItem } = useQuoteStore();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (items.length === 0) { setProducts([]); setLoading(false); return; }
      setLoading(true);
      try {
        const results = await Promise.all(
          items.map((item) => fetch(`${API}/api/products/${item.product_id}`).then((r) => r.json()))
        );
        setProducts(results.filter((p) => p && p.id));
      } catch (error) { 
        console.error('Failed to fetch wishlist:', error); 
      } finally { 
        setLoading(false); 
      }
    };
    fetchProducts();
  }, [items]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Heart className="w-16 h-16 mx-auto mb-6 text-muted-foreground" strokeWidth={1} />
          <h1 className="text-h2 mb-4 font-extralight">{t.auth?.loginRequired || 'Sign in to view your wishlist'}</h1>
          <Link to={`/${language}/login`}>
            <Button className="btn-primary rounded-none h-11 px-6 font-medium text-xs uppercase tracking-widest">
              {t.auth.login}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-left font-light" data-testid="wishlist-page">
      <div className="bg-surface section-padding border-b border-gray-50">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-caption text-muted-foreground mb-3 text-xs tracking-wider uppercase text-gray-400 font-semibold">
              {t.lookbook?.wishlistEyebrow || 'Saved Items'}
            </p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-black">
              {t.lookbook?.wishlistTitle || 'Wishlist'}
            </h1>
          </motion.div>
        </div>
      </div>
      
      <div className="container-custom py-16">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-muted mb-4 rounded-none" />
                <div className="h-4 bg-muted w-3/4 mb-2 rounded-none" />
                <div className="h-4 bg-muted w-1/2 rounded-none" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-gray-200 bg-gray-50/50">
            <Heart className="w-16 h-16 mx-auto mb-6 text-neutral-300" strokeWidth={1} />
            <h2 className="text-xl font-light text-muted-foreground mb-6">{t.common?.noResults || 'Your wishlist is empty'}</h2>
            <Link to={`/${language}/products`}>
              <Button className="btn-primary rounded-none h-11 px-6 text-xs uppercase tracking-widest font-medium">
                {t?.common?.exploreProducts || 'Explore Products'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {products.map((product) => {
              const primaryImg = product.images?.find((img) => img.is_primary) || product.images?.[0];
              return (
                <motion.div key={product.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="group relative border border-gray-100 p-4 bg-white shadow-sm transition-all hover:shadow-md">
                  <Link to={`/${language}/products/${product.id}`} data-testid={`wishlist-item-${product.id}`}>
                    <div className="aspect-[3/4] overflow-hidden bg-muted mb-4 rounded-none relative">
                      {primaryImg?.url ? (
                        <img 
                          src={primaryImg.url} 
                          alt={getLocalized(product.name, language)} 
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                          <ImageOff className="w-6 h-6 mb-2" strokeWidth={1} />
                          <span className="text-[10px] uppercase tracking-widest">No Image</span>
                        </div>
                      )}
                    </div>
                    <h3 className="font-light text-base text-gray-900 mb-1 line-clamp-1">{getLocalized(product.name, language)}</h3>
                    <p className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">
                      {getLocalized(product.short_description, language) || t.categories?.[product.category]}
                    </p>
                  </Link>
                  <div className="absolute top-6 right-6 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button 
                      onClick={() => { removeFromWishlist(product.id); toast.success('Removed from wishlist'); }} 
                      className="w-10 h-10 bg-white/90 border border-gray-200 backdrop-blur-sm flex items-center justify-center hover:bg-destructive hover:text-white transition-colors text-neutral-600 shadow-sm" 
                      data-testid={`remove-wishlist-${product.id}`}
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button 
                      onClick={() => { addItem(product, 1); toast.success('Added to project specification'); }} 
                      className="w-10 h-10 bg-white/90 border border-gray-200 backdrop-blur-sm flex items-center justify-center hover:bg-black hover:text-white transition-colors text-neutral-600 shadow-sm" 
                      data-testid={`add-to-quote-${product.id}`}
                      title="Add to Specification"
                    >
                      <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const QuotePage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const { items, removeItem, updateQuantity, clearItems } = useQuoteStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quoteId, setQuoteId] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: '',
    delivery_address: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product.id,
          sku: i.product.sku || i.product.id,
          name: getLocalized(i.product.name, language),
          quantity: i.quantity,
          price: i.product.price,
          selected_material: getLocalized(i.product.material, language) || null,
          selected_finish: getLocalized(i.product.finish, language) || null,
        })),
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        notes: formData.notes,
        delivery_address: formData.delivery_address,
      };

      const res = await fetch(`${API}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed');
      const result = await res.json();
      setQuoteId(result.quote_id);
      setSubmitted(true);
      clearItems();
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally { 
      setLoading(false); 
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="quote-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md px-6">
          <div className="w-16 h-16 bg-black text-white rounded-none flex items-center justify-center mx-auto mb-8">
            <Check className="w-8 h-8" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-light text-gray-900 mb-4">
            {language === 'ru' ? 'Ваш запрос успешно принят! Наш архитектор свяжется с вами в ближайшее время.' : t.cart?.successMessage || 'Your request has been received! Our architect will contact you shortly.'}
          </h1>
          <p className="text-xs font-mono text-muted-foreground mb-2">ID: {quoteId}</p>
          <Link to={`/${language}/products`}>
            <Button className="btn-primary mt-6 rounded-none h-11 px-6 text-xs uppercase tracking-widest font-medium">
              {t.common?.exploreProducts || 'Open Lookbook'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-left font-light" data-testid="quote-page">
      <div className="bg-surface section-padding border-b border-gray-50">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-caption text-muted-foreground mb-3 text-xs tracking-wider uppercase text-gray-400 font-semibold">
              {language === 'ru' ? 'Запрос спецификации' : t.common?.requestQuote || 'Concept Proposal Request'}
            </p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-black">
              {language === 'ru' ? 'Спецификация проекта' : language === 'nl' ? 'Projectspecificatie' : language === 'ro' ? 'Specificație proiect' : 'Project Specification'}
            </h1>
          </motion.div>
        </div>
      </div>

      <div className="container-custom py-16">
        {items.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-gray-200 bg-gray-50/50">
            <ShoppingBag className="w-16 h-16 mx-auto mb-6 text-neutral-300" strokeWidth={1} />
            <h2 className="text-xl font-light text-muted-foreground mb-6">{language === 'ru' ? 'Спецификация проекта пуста' : t.cart?.empty || 'No pieces selected yet'}</h2>
            <Link to={`/${language}/products`}>
              <Button className="btn-primary rounded-none h-11 px-6 text-xs uppercase tracking-widest font-medium">
                {t.b2bDashboard?.browseProducts || 'Open Lookbook'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-xl font-normal text-gray-900 border-b border-gray-100 pb-3">
                {language === 'ru' ? 'Выбранные элементы' : language === 'nl' ? 'Geselecteerde elementen' : language === 'ro' ? 'Articole selectate' : 'Selected Pieces'} ({items.length})
              </h2>
              <div className="space-y-4">
                {items.map((item) => {
                  const pImg = item.product?.images?.[0];
                  return (
                    <div key={item.product.id} className="flex gap-6 p-5 border border-border bg-white shadow-sm relative group text-left" data-testid={`quote-item-${item.product.id}`}>
                      <div className="w-24 h-24 bg-muted shrink-0 flex items-center justify-center overflow-hidden border border-gray-100">
                        {pImg?.url ? (
                          <img src={pImg.url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-muted-foreground text-[10px] text-center px-1 font-mono uppercase">{item.product.sku}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-4">
                          <div className="min-w-0">
                            <Link to={`/${language}/products/${item.product.id}`} className="font-light text-lg text-gray-900 hover:underline block truncate">
                              {getLocalized(item.product.name, language)}
                            </Link>
                            <p className="text-xs font-mono text-muted-foreground mt-1">{item.product.sku}</p>
                            
                            {/* БАЗОВЫЕ МАТЕРИАЛЫ КАРТОЧКИ ТОВАРА НА ЯЗЫКЕ САЙТА */}
                            <div className="mt-2 space-y-1">
                              {item.product.material && (
                                <p className="text-xs text-gray-700 font-normal">
                                  <span className="text-[10px] text-neutral-400 uppercase tracking-wider mr-1">
                                    {language === 'ru' ? 'Базовый материал' : language === 'nl' ? 'Basismateriaal' : language === 'ro' ? 'Material de bază' : 'Base Material'}:
                                  </span>
                                  {getLocalized(item.product.material, language)}
                                </p>
                              )}
                              {item.product.finish && (
                                <p className="text-xs text-gray-700 font-normal">
                                  <span className="text-[10px] text-neutral-400 uppercase tracking-wider mr-1">
                                    {language === 'ru' ? 'Базовое покрытие' : language === 'nl' ? 'Basiscoating' : language === 'ro' ? 'Finisaj de bază' : 'Base Coating'}:
                                  </span>
                                  {getLocalized(item.product.finish, language)}
                                </p>
                              )}
                            </div>
                          </div>
                          <button onClick={() => removeItem(item.product.id)} className="text-muted-foreground hover:text-destructive p-1 transition-colors" data-testid={`remove-quote-${item.product.id}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center border border-gray-200 bg-white">
                            <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-50 transition-colors border-r border-gray-200"><Minus className="w-3 h-3" /></button>
                            <span className="w-12 text-center text-sm font-mono text-gray-900">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-50 transition-colors border-l border-gray-200"><Plus className="w-3 h-3" /></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-surface p-8 border border-border sticky top-28 bg-neutral-50/50">
              <h3 className="text-lg font-normal text-gray-900 mb-4">{language === 'ru' ? 'Ваш запрос' : t.projectSpec?.summary || 'Your Request'}</h3>
              <div className="space-y-4 mb-6">
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs text-muted-foreground leading-relaxed font-light">
                    {t.projectSpec?.footerDesc || 'No pricing shown — every project is bespoke. Our architect will prepare a personalised concept and indicative budget after a conversation with you.'}
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs font-medium text-gray-700">{t.contact?.name || 'Full Name'} *</Label>
                  <Input value={formData.customer_name} onChange={(e) => setFormData(p => ({...p, customer_name: e.target.value}))} className="h-11 mt-1.5 rounded-none bg-white text-gray-900 focus-visible:border-black" required data-testid="quote-name" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-700">{t.contact?.email || 'Email'} *</Label>
                  <Input type="email" value={formData.customer_email} onChange={(e) => setFormData(p => ({...p, customer_email: e.target.value}))} className="h-11 mt-1.5 rounded-none bg-white text-gray-900 focus-visible:border-black" required data-testid="quote-email" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-700">{t.contact?.phone || 'Phone'} *</Label>
                  <Input type="tel" value={formData.customer_phone} onChange={(e) => setFormData(p => ({...p, customer_phone: e.target.value}))} className="h-11 mt-1.5 rounded-none bg-white text-gray-900 focus-visible:border-black" required data-testid="quote-phone" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-700">{t.account?.streetAddress || 'Delivery Address'}</Label>
                  <Input value={formData.delivery_address} onChange={(e) => setFormData(p => ({...p, delivery_address: e.target.value}))} className="h-11 mt-1.5 rounded-none bg-white text-gray-900 focus-visible:border-black" data-testid="quote-address" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-700">{t.projectSpec?.notes || 'Notes'}</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData(p => ({...p, notes: e.target.value}))} className="mt-1.5 rounded-none bg-white text-gray-900 focus-visible:border-black" rows={3} data-testid="quote-notes" />
                </div>
                <Button type="submit" className="btn-primary w-full h-11 rounded-none text-xs uppercase tracking-widest font-semibold mt-2" disabled={loading} data-testid="submit-quote">
                  {loading ? '...' : (t.projectSpec?.modalTitle || 'REQUEST CONCEPT')}
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
